import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    // If post not found in our static data, we could potentially fetch it or show 404
    // For now, let's redirect to blog or show a simple 404 message
    return (
      <div className="py-20 text-center container mx-auto px-4">
        <h1 className="text-4xl font-bold mb-4">Siden blev ikke fundet</h1>
        <p className="text-slate-600 mb-8">Vi beklager, men indlægget du leder efter findes ikke længere.</p>
        <Link to="/blog" className="text-[#b60f00] font-bold hover:underline flex items-center justify-center">
          <ArrowLeft size={18} className="mr-2" /> Tilbage til bloggen
        </Link>
      </div>
    );
  }

  return (
    <article className="py-16 bg-white">
      <Helmet>
        <title>{post.title}</title>
        <meta name="description" content={post.description} />
      </Helmet>

      <div className="container mx-auto px-4 max-w-4xl">
        <nav className="text-sm text-slate-500 mb-8 flex items-center space-x-2">
          <Link to="/" className="hover:text-[#b60f00]">Forsiden</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-[#b60f00]">Økonomiblog</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium truncate max-w-[200px] md:max-w-none">
            {post.title.split('|')[0].trim()}
          </span>
        </nav>

        <header className="mb-12">
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
            {post.title.split('|')[0].trim()}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-slate-500 border-b border-slate-100 pb-8">
            <div className="flex items-center">
              <Calendar size={18} className="mr-2 text-[#b60f00]" />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center">
              <User size={18} className="mr-2 text-[#b60f00]" />
              <span>{post.author}</span>
            </div>
            <button className="flex items-center hover:text-[#b60f00] transition-colors ml-auto">
              <Share2 size={18} className="mr-2" />
              <span>Del indlæg</span>
            </button>
          </div>
        </header>

        {post.image && (
          <div className="mb-12 rounded-3xl overflow-hidden shadow-xl aspect-[16/9]">
            <img 
              src={post.image} 
              alt={post.title} 
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div 
          className="prose prose-slate prose-lg max-w-none prose-headings:text-slate-900 prose-a:text-[#b60f00] prose-strong:text-slate-900 prose-img:rounded-2xl"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <footer className="mt-16 pt-12 border-t border-slate-100">
          <div className="bg-slate-50 rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Har du spørgsmål til emnet?</h3>
              <p className="text-slate-600">Vi sidder klar til at hjælpe dig med uvildig rådgivning.</p>
            </div>
            <Link 
              to="/kontakt-os" 
              className="bg-[#b60f00] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#a00d00] transition-all shadow-md active:scale-95 whitespace-nowrap"
            >
              Kontakt os i dag
            </Link>
          </div>
          
          <div className="mt-12">
            <Link to="/blog" className="inline-flex items-center text-slate-500 hover:text-[#b60f00] font-semibold transition-colors">
              <ArrowLeft size={18} className="mr-2" /> Se alle blogindlæg
            </Link>
          </div>
        </footer>
      </div>
    </article>
  );
};

export default BlogPost;
